// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useMerchantAccountStore from '@/app/store/modules/merchantAccount';
import actionsMerchantAccounts from '../../../api/actionsMerchantAccounts';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import RanksUpdate from '@/app/modules/contracts/components/ranksUpdate/RanksUpdate';
import ContractsFromProviderTable from '@/app/modules/contracts/components/tables/ContractsFromProviderTable';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { excludeRepeatedValues } from '@/common-utils/functions/repeatedValues';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Style
import './ModalCreateMerchantAccount.scss';
// Lang
import { nsAccount } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 *  @returns {JSX} ModalCreateMerchantAccount
 */
function ModalCreateMerchantAccount({ isOpen, setIsOpen }) {

    // Constants
    const STEPS_NUMBER = 4;
    const STEP_TITLE_PREFIX = 'modal.create_merchant_account.steps';

    // Toast
    const { toast } = useToast();

    // Store
    const storeRefetchMerchantAccounts = useMerchantAccountStore(s => s.storeRefetchMerchantAccounts);
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // State
    const [step, setStep] = useState(0);
    const [contractsOnError, setContractsOnError] = useState([]);

    // Table selection
    const [contractsSelected, setContractsSelected] = useState(getInitialValueForRowSelected());

    // Form definition
    const formSchema = z.object({
        email: z.string().email(),
        label: z.string().min(1).max(100)
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            email: '',
            label: ''
        }
    });

    // API Calls
    const onSuccess = () => {
        storeRefetchMerchantAccounts(true);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.create_merchant_account.success' namespace={nsAccount} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.create_merchant_account.error' namespace={nsAccount} />,
            variant: 'danger'
        });
    };
    const submitCreateMerchantAccount = actionsMerchantAccounts.createMerchantAccount(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.form`,
                disabledNext: Boolean(form.getValues().email === '' || form.getValues().label === '' || form.formState.errors.email || form.formState.errors.label)
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.contract_selection`,
                disabledNext: contractsSelected.rowsData.length === 0
            },
            2: {
                stepTitle: `${STEP_TITLE_PREFIX}.contract_edition`,
                disabledNext: false
            },
            3: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirmation`,
                disabledNext: false
            }
        }),
        [form.getValues(), form.formState.errors.email, form.formState.errors.label, contractsSelected.rowsData]
    );

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn(step !== 1 ? 'max-w-3xl' : 'max-w-6xl')}>
                <HeaderTitle title='modal.create_merchant_account.title' namespace={nsAccount} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsAccount}
                        className={'mb-2 flex'}
                    />
                    {
                        step === 0 &&
                        <Form {...form}>
                            <form className='space-y-4'>
                                <div className='flex flex-col m-2'>
                                    <div className='flex-1 my-2'>
                                        <FormField
                                            control={form.control}
                                            name='email'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel required>
                                                        <Entity namespace={nsAccount} entity='modal.create_merchant_account.labels.email' />
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type='email'
                                                            namespace={nsAccount}
                                                            entity='modal.create_merchant_account.placeholders.email'
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className='flex-1 my-2'>
                                        <FormField
                                            control={form.control}
                                            name='label'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel required>
                                                        <Entity namespace={nsAccount} entity='modal.create_merchant_account.labels.label' />
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            namespace={nsAccount}
                                                            entity='modal.create_merchant_account.placeholders.label'
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            </form>
                        </Form>
                    }
                    {
                        step === 1 &&
                        <ContractsFromProviderTable
                            filtersEventName={setFiltersEventName('contracts_from_provider', true)}
                            rowsSelected={contractsSelected}
                            setRowsSelected={setContractsSelected}
                        />

                    }
                    {
                        step === 2 &&
                        contractsSelected.rowsData && excludeRepeatedValues(contractsSelected.rowsData,
                            contract => [contract.contract, contract.application, contract.number, contract.bankCode]).map((contract) => (
                                <RanksUpdate contract={contract} key={contract.uuid} contractsOnError={contractsOnError} setContractsOnError={setContractsOnError} />
                            ))
                    }
                    {
                        step === 3 &&
                        <>
                            <Entity
                                entity={'modal.create_merchant_account.texts.confirmation'}
                                namespace={nsAccount}
                                data={{ label: form.getValues().label, contracts: contractsSelected.rowsData.length }}
                            />
                            <div className='mt-2'>
                                {
                                    contractsSelected.rowsData && excludeRepeatedValues(contractsSelected.rowsData,
                                        contract => [contract.contract, contract.application, contract.number, contract.bankCode]).map((contract) => (
                                            <div className='ml-8' key={contract.uuid}>
                                                <Entity
                                                    entity={'modal.create_merchant_account.texts.contract'}
                                                    namespace={nsAccount}
                                                    data={{ contractNumber: contract.number }}
                                                />
                                            </div>
                                        ))
                                }
                            </div>
                        </>
                    }

                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={() => {
                        submitCreateMerchantAccount.mutate({
                            provider: rootOrganization.uuid,
                            label: form.getValues().label,
                            email: form.getValues().email,
                            contractProfiles: contractsSelected.rowsData.map((contract => ({
                                contract: contract.uuid,
                                merchantLabel: contract.merchantLabel ?? null,
                                rank: contract.newRank?.includes(',') ? contract.newRank.replaceAll(' ', '').split(',') : contract.newRank
                            })))
                        });
                    }
                    }
                    isLoading={submitCreateMerchantAccount.isLoading}
                    disableValidation={disabledNext || (step === 2 && contractsOnError.length > 0)}
                />
            </ModalContent>
        </Modal>
    );
}
ModalCreateMerchantAccount.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalCreateMerchantAccount;